import React from 'react';
import { Switch } from '@/components/ui/switch';
import { useTranslation } from 'react-i18next';
import { MessageSquare } from 'lucide-react';

interface SmsConsentFormProps {
  smsAppointmentReminder: boolean;
  smsBookingUpdate: boolean;
  onSmsAppointmentReminderChange: (checked: boolean) => void;
  onSmsBookingUpdateChange: (checked: boolean) => void;
}

const SmsConsentForm: React.FC<SmsConsentFormProps> = ({
  smsAppointmentReminder,
  smsBookingUpdate,
  onSmsAppointmentReminderChange,
  onSmsBookingUpdateChange,
}) => {
  const { t } = useTranslation('settings');

  const hasSmsEnabled = smsAppointmentReminder || smsBookingUpdate;

  return (
    <div className="space-y-3 sm:space-y-4 pt-4 border-t">
      <div className="flex items-center gap-2 mb-2">
        <MessageSquare className="w-4 h-4 text-muted-foreground" />
        <span className="font-medium text-sm sm:text-base">{t('notifications.smsNotifications')}</span>
      </div>

      <div className="flex items-start sm:items-center justify-between gap-3">
        <div className="space-y-0.5 flex-1">
          <div className="font-medium text-sm sm:text-base">{t('notifications.smsAppointmentReminder')}</div>
          <div className="text-xs sm:text-sm text-muted-foreground">
            {t('notifications.smsAppointmentReminderDescription')}
          </div>
        </div>
        <div className="flex-shrink-0">
          <Switch
            checked={smsAppointmentReminder}
            onCheckedChange={onSmsAppointmentReminderChange}
          />
        </div>
      </div>

      <div className="flex items-start sm:items-center justify-between gap-3">
        <div className="space-y-0.5 flex-1">
          <div className="font-medium text-sm sm:text-base">{t('notifications.smsBookingUpdate')}</div>
          <div className="text-xs sm:text-sm text-muted-foreground">
            {t('notifications.smsBookingUpdateDescription')}
          </div>
        </div>
        <div className="flex-shrink-0">
          <Switch
            checked={smsBookingUpdate}
            onCheckedChange={onSmsBookingUpdateChange}
          />
        </div>
      </div>

      {hasSmsEnabled && (
        <div className="rounded-md bg-muted/50 p-3 text-xs text-muted-foreground leading-relaxed">
          <p>{t('notifications.smsConsentDisclosure')}</p>
          <p className="mt-1">
            {t('notifications.smsConsentLinks')
              .replace('{privacyPolicy}', '')
              .replace('{termsOfService}', '')
              .split('')
              .filter(Boolean)
              .length > 0 ? null : null}
            {/* Render with actual links */}
            See our{' '}
            <a
              href="https://resonow.org/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="underline text-primary hover:text-primary/80"
            >
              {t('notifications.privacyPolicyLink')}
            </a>
            {' and '}
            <a
              href="https://resonow.org/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="underline text-primary hover:text-primary/80"
            >
              {t('notifications.termsOfServiceLink')}
            </a>.
          </p>
        </div>
      )}
    </div>
  );
};

export default SmsConsentForm;
